import { useEffect } from "react";

const interactiveSurfaceSelector = [
  ".button",
  ".header-signin",
  ".site-nav-link",
  ".hero-command-panel",
  ".home-principle",
  ".home-offer-row",
  ".home-trusted-logo",
  ".market-card",
  ".offer-card",
  ".hero-side-panel",
  ".fit-panel",
  ".audience-card",
  ".value-card",
  ".feature-card",
  ".media-card",
  ".content-panel",
  ".gradient-panel",
  ".job-card",
  ".event-row-card",
  ".team-card",
  ".use-case-card",
  ".overview-card",
  ".product-banner",
  ".capability-card",
  ".conference-summary",
  ".platform-layer-card",
  ".product-screenshot-card",
  ".product-screenshot-frame",
  ".proof-metric",
  ".launch-step-card",
  ".latest-card",
  ".publication-card",
  ".trusted-partner-card",
  ".solution-product-row",
  ".contact-trust-panel",
  ".linkedin-update-card",
  ".linkedin-source-panel",
  ".youtube-video-card",
  ".youtube-feature-panel"
].join(",");

export function SiteInteractionLayer() {
  useEffect(() => {
    const root = document.documentElement;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    if (reduceMotion.matches) {
      root.style.setProperty("--pointer-x", "50%");
      root.style.setProperty("--pointer-y", "18%");
      root.style.setProperty("--scroll-progress", "0%");
      root.style.setProperty("--scroll-ratio", "0");
      return undefined;
    }

    let frame = 0;
    let activeSurface: HTMLElement | null = null;
    const hoverGlow = document.createElement("span");

    hoverGlow.className = "interactive-hover-glow";
    hoverGlow.setAttribute("aria-hidden", "true");

    const updateScroll = () => {
      const scrollRange = document.documentElement.scrollHeight - window.innerHeight;
      const progress = scrollRange > 0 ? (window.scrollY / scrollRange) * 100 : 0;
      const clampedProgress = Math.min(100, Math.max(0, progress));
      root.style.setProperty("--scroll-progress", `${clampedProgress}%`);
      root.style.setProperty("--scroll-ratio", `${clampedProgress / 100}`);
    };

    const updatePointer = (event: PointerEvent) => {
      if (event.pointerType === "touch") {
        return;
      }

      if (frame) {
        cancelAnimationFrame(frame);
      }

      frame = requestAnimationFrame(() => {
        root.style.setProperty("--pointer-x", `${event.clientX}px`);
        root.style.setProperty("--pointer-y", `${event.clientY}px`);
      });
    };

    const clearActiveSurface = () => {
      if (!activeSurface) {
        return;
      }

      activeSurface.classList.remove("is-pointer-active");
      activeSurface.style.removeProperty("--hover-x");
      activeSurface.style.removeProperty("--hover-y");
      activeSurface.style.removeProperty("--tilt-x");
      activeSurface.style.removeProperty("--tilt-y");
      activeSurface = null;
      hoverGlow.remove();
    };

    const updateInteractiveSurface = (event: PointerEvent) => {
      if (event.pointerType === "touch") {
        clearActiveSurface();
        return;
      }

      const target = event.target instanceof Element ? event.target.closest<HTMLElement>(interactiveSurfaceSelector) : null;

      if (!target) {
        clearActiveSurface();
        return;
      }

      if (target !== activeSurface) {
        clearActiveSurface();
        activeSurface = target;
        activeSurface.classList.add("is-pointer-active");
        activeSurface.prepend(hoverGlow);
      }

      const rect = target.getBoundingClientRect();
      const localX = rect.width > 0 ? (event.clientX - rect.left) / rect.width : 0.5;
      const localY = rect.height > 0 ? (event.clientY - rect.top) / rect.height : 0.5;
      const clampedX = Math.min(1, Math.max(0, localX));
      const clampedY = Math.min(1, Math.max(0, localY));
      const tiltX = (0.5 - clampedY) * 4;
      const tiltY = (clampedX - 0.5) * 5;

      target.style.setProperty("--hover-x", `${clampedX * 100}%`);
      target.style.setProperty("--hover-y", `${clampedY * 100}%`);
      target.style.setProperty("--tilt-x", `${tiltX.toFixed(2)}deg`);
      target.style.setProperty("--tilt-y", `${tiltY.toFixed(2)}deg`);
    };

    const clearWhenLeavingWindow = (event: PointerEvent) => {
      if (!event.relatedTarget) {
        clearActiveSurface();
      }
    };

    updateScroll();
    root.style.setProperty("--pointer-x", `${window.innerWidth * 0.72}px`);
    root.style.setProperty("--pointer-y", `${window.innerHeight * 0.34}px`);

    window.addEventListener("scroll", updateScroll, { passive: true });
    window.addEventListener("resize", updateScroll, { passive: true });
    window.addEventListener("pointermove", updatePointer, { passive: true });
    document.addEventListener("pointermove", updateInteractiveSurface, { passive: true });
    document.addEventListener("pointerout", clearWhenLeavingWindow, { passive: true });
    window.addEventListener("blur", clearActiveSurface);
    window.addEventListener("scroll", clearActiveSurface, { passive: true });

    return () => {
      if (frame) {
        cancelAnimationFrame(frame);
      }
      clearActiveSurface();
      window.removeEventListener("scroll", updateScroll);
      window.removeEventListener("resize", updateScroll);
      window.removeEventListener("pointermove", updatePointer);
      document.removeEventListener("pointermove", updateInteractiveSurface);
      document.removeEventListener("pointerout", clearWhenLeavingWindow);
      window.removeEventListener("blur", clearActiveSurface);
      window.removeEventListener("scroll", clearActiveSurface);
    };
  }, []);

  return (
    <div className="site-interaction-layer" aria-hidden="true">
      <div className="scroll-progress-rail" />
      <div className="cursor-aura" />
      <div className="ambient-security-field">
        <div className="ambient-security-grid ambient-security-grid--primary" />
        <div className="ambient-security-grid ambient-security-grid--secondary" />
        <div className="ambient-scan-band ambient-scan-band--one" />
        <div className="ambient-scan-band ambient-scan-band--two" />
        <div className="ambient-data-rain ambient-data-rain--one">
          <span />
          <span />
          <span />
        </div>
        <div className="ambient-data-rain ambient-data-rain--two">
          <span />
          <span />
          <span />
        </div>
        <div className="ambient-holocron-fragment ambient-holocron-fragment--one">
          <span />
          <span />
        </div>
        <div className="ambient-holocron-fragment ambient-holocron-fragment--two">
          <span />
          <span />
        </div>
        <div className="ambient-holocron-fragment ambient-holocron-fragment--three">
          <span />
          <span />
        </div>
      </div>
      <div className="ambient-orbit ambient-orbit--one" />
      <div className="ambient-orbit ambient-orbit--two" />
    </div>
  );
}
